import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../services/api';

// Configuration
const AI_CONFIG = {
  DEFAULT_MODEL: 'claude-sonnet',
  CACHE_DURATION: 24 * 60 * 60 * 1000, // 24 hours
  STORAGE_KEY: 'pastely_selected_model',
  CACHE_KEY: 'pastely_models_cache'
};

// Initial state
const initialState = {
  availableModels: null,
  selectedModel: AI_CONFIG.DEFAULT_MODEL,
  isLoading: false,
  error: null,
  isInitialized: false
};

// Action types
const ActionTypes = {
  FETCH_MODELS_START: 'FETCH_MODELS_START',
  FETCH_MODELS_SUCCESS: 'FETCH_MODELS_SUCCESS',
  FETCH_MODELS_ERROR: 'FETCH_MODELS_ERROR',
  SET_SELECTED_MODEL: 'SET_SELECTED_MODEL',
  RESET_TO_DEFAULT: 'RESET_TO_DEFAULT',
  INITIALIZE: 'INITIALIZE'
};

// Reducer
function modelReducer(state, action) {
  switch (action.type) {
    case ActionTypes.FETCH_MODELS_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case ActionTypes.FETCH_MODELS_SUCCESS:
      return {
        ...state,
        availableModels: action.payload,
        isLoading: false,
        error: null,
        isInitialized: true
      };
    
    case ActionTypes.FETCH_MODELS_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        isInitialized: true
      };
    
    case ActionTypes.SET_SELECTED_MODEL:
      return {
        ...state,
        selectedModel: action.payload
      };
    
    case ActionTypes.RESET_TO_DEFAULT:
      return {
        ...state,
        selectedModel: AI_CONFIG.DEFAULT_MODEL
      };
    
    case ActionTypes.INITIALIZE:
      return {
        ...state,
        selectedModel: action.payload,
        isInitialized: true
      };
    
    default:
      return state;
  }
}

// Context
const ModelContext = createContext();

// Provider component
export function ModelProvider({ children }) {
  const [state, dispatch] = useReducer(modelReducer, initialState);

  // Initialize from localStorage
  useEffect(() => {
    const savedModel = localStorage.getItem(AI_CONFIG.STORAGE_KEY);
    if (savedModel) {
      dispatch({ type: ActionTypes.INITIALIZE, payload: savedModel });
    } else {
      dispatch({ type: ActionTypes.INITIALIZE, payload: AI_CONFIG.DEFAULT_MODEL });
    }
  }, []);

  // Fetch models on mount
  useEffect(() => {
    fetchModels();
  }, []);

  // Save selected model to localStorage
  useEffect(() => {
    if (state.isInitialized) {
      localStorage.setItem(AI_CONFIG.STORAGE_KEY, state.selectedModel);
    }
  }, [state.selectedModel, state.isInitialized]);

  // Check cache validity
  const isCacheValid = () => {
    const cacheData = localStorage.getItem(AI_CONFIG.CACHE_KEY);
    if (!cacheData) return false;
    
    try {
      const { timestamp } = JSON.parse(cacheData);
      return Date.now() - timestamp < AI_CONFIG.CACHE_DURATION;
    } catch {
      return false;
    }
  };

  // Get cached models
  const getCachedModels = () => {
    try {
      const cacheData = localStorage.getItem(AI_CONFIG.CACHE_KEY);
      if (cacheData) {
        const { data } = JSON.parse(cacheData);
        return data;
      }
    } catch {
      // Invalid cache data
    }
    return null;
  };

  // Cache models
  const cacheModels = (models) => {
    try {
      const cacheData = {
        data: models,
        timestamp: Date.now()
      };
      localStorage.setItem(AI_CONFIG.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache models:', error);
    }
  };

  // Fetch models from API
  const fetchModels = async () => {
    // Check cache first
    if (isCacheValid()) {
      const cachedModels = getCachedModels();
      if (cachedModels) {
        dispatch({ type: ActionTypes.FETCH_MODELS_SUCCESS, payload: cachedModels });
        return;
      }
    }

    dispatch({ type: ActionTypes.FETCH_MODELS_START });
    
    try {
      const models = await api.models.getAll();
      dispatch({ type: ActionTypes.FETCH_MODELS_SUCCESS, payload: models });
      cacheModels(models);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      
      // Provide fallback models if API fails
      const fallbackModels = {
        defaultModel: AI_CONFIG.DEFAULT_MODEL,
        supportedModels: [
          'nova-micro', 'nova-lite', 'nova-pro',
          'claude-haiku', 'claude-sonnet', 'claude-opus',
          'deepseek-r1', 'deepseek-coder', 'deepseek-math',
          'gpt-4o-mini', 'gpt-4o'
        ],
        modelDetails: {
          // Amazon Models
          'nova-micro': {
            displayName: 'Nova Micro',
            description: 'Ultra-fast responses for simple tasks and quick suggestions',
            provider: 'Amazon'
          },
          'nova-lite': {
            displayName: 'Nova Lite',
            description: 'Balanced speed and quality for everyday coding tasks',
            provider: 'Amazon'
          },
          'nova-pro': {
            displayName: 'Nova Pro',
            description: 'Advanced reasoning for complex code analysis and generation',
            provider: 'Amazon'
          },
          
          // Anthropic Models
          'claude-haiku': {
            displayName: 'Claude 3 Haiku',
            description: 'Fast and efficient for quick code snippets and simple tasks',
            provider: 'Anthropic'
          },
          'claude-sonnet': {
            displayName: 'Claude 3.5 Sonnet',
            description: 'Excellent balance of speed, intelligence, and cost-effectiveness',
            provider: 'Anthropic'
          },
          'claude-opus': {
            displayName: 'Claude 3 Opus',
            description: 'Most capable model for complex reasoning and analysis',
            provider: 'Anthropic'
          },
          
          // DeepSeek Models
          'deepseek-r1': {
            displayName: 'DeepSeek R1',
            description: 'Advanced reasoning model with strong problem-solving capabilities',
            provider: 'DeepSeek'
          },
          'deepseek-coder': {
            displayName: 'DeepSeek Coder',
            description: 'Specialized for code generation, debugging, and optimization',
            provider: 'DeepSeek'
          },
          'deepseek-math': {
            displayName: 'DeepSeek Math',
            description: 'Optimized for mathematical reasoning and algorithmic problems',
            provider: 'DeepSeek'
          },
          
          // OpenAI Models
          'gpt-4o-mini': {
            displayName: 'GPT-4o Mini',
            description: 'Cost-effective model with good performance for most tasks',
            provider: 'OpenAI'
          },
          'gpt-4o': {
            displayName: 'GPT-4o',
            description: 'Latest multimodal model with advanced reasoning capabilities',
            provider: 'OpenAI'
          }
        }
      };
      
      dispatch({ type: ActionTypes.FETCH_MODELS_SUCCESS, payload: fallbackModels });
      dispatch({ type: ActionTypes.FETCH_MODELS_ERROR, payload: error.message });
    }
  };

  // Set selected model
  const setSelectedModel = (modelName) => {
    if (!modelName) return;
    
    // Validate model exists
    if (state.availableModels && 
        !state.availableModels.supportedModels.includes(modelName)) {
      console.warn(`Model ${modelName} is not supported`);
      return;
    }
    
    dispatch({ type: ActionTypes.SET_SELECTED_MODEL, payload: modelName });
  };

  // Reset to default model
  const resetToDefault = () => {
    dispatch({ type: ActionTypes.RESET_TO_DEFAULT });
  };

  // Get selected model info
  const getSelectedModelInfo = () => {
    if (!state.availableModels || !state.selectedModel) {
      return null;
    }
    
    return state.availableModels.modelDetails[state.selectedModel] || null;
  };

  // Retry fetching models
  const retryFetch = () => {
    fetchModels();
  };

  const value = {
    // State
    availableModels: state.availableModels,
    selectedModel: state.selectedModel,
    isLoading: state.isLoading,
    error: state.error,
    isInitialized: state.isInitialized,
    
    // Actions
    setSelectedModel,
    resetToDefault,
    retryFetch,
    
    // Computed values
    getSelectedModelInfo
  };

  return (
    <ModelContext.Provider value={value}>
      {children}
    </ModelContext.Provider>
  );
}

// Hook to use the model context
export function useModel() {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModel must be used within a ModelProvider');
  }
  return context;
}

export default ModelContext;
